import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { fmtRelative } from '@/features/dossier/components/primitives'
import { listPlatforms, listWatches, saveWatch, updateWatch,
         type Platform, type Watch } from './api'

/**
 * Radar — what we are listening for, and how loud it must get.
 *
 * ## A watch defines an ALERT EVENT, not a search
 *
 * The two velocity fields together are the trigger: "this many engagements inside this many
 * minutes." Volume alone would fire on every ordinary mention of a well-known name and the room
 * would learn to ignore it; rate is what separates a comment from a wave. The defaults (50 in
 * 60 minutes) are a starting point, not a recommendation — the right number depends entirely on
 * how much the subject is normally talked about.
 *
 * ## A watch that errored is not a watch that found nothing
 *
 * `lastStatus` is shown on the row for exactly this reason. Silence from a broken watch looks
 * identical to calm, and only one of those means it is safe to stop looking.
 */
export function WatchesPage() {
  const [watches, setWatches] = useState<Watch[] | null>(null)
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  const [subjectId, setSubjectId] = useState('')
  const [watchLabel, setWatchLabel] = useState('')
  const [terms, setTerms] = useState('')
  const [picked, setPicked] = useState<string[]>([])
  const [velocityEngagements, setVelocityEngagements] = useState(50)
  const [velocityMinutes, setVelocityMinutes] = useState(60)
  const [pollSeconds, setPollSeconds] = useState(300)

  const load = useCallback(async () => {
    try {
      setError(null)
      setWatches(await listWatches())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listPlatforms().then((rows) => {
      setPlatforms(rows)
      setPicked((c) => c.length ? c : rows.filter((p) => p.code !== 'MANUAL').map((p) => p.code))
    }).catch(() => setPlatforms([]))
    void listSubjects({ size: '100' }).then((rows) => {
      setSubjects(rows)
      setSubjectId((c) => c || rows[0]?.id || '')
    }).catch(() => setSubjects([]))
  }, [])

  if (error && !watches) return <ErrorStrip message={error} />
  if (!watches) return <LoadingBlock label="Loading watches" />

  async function create(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true); setError(null)
    try {
      await saveWatch({
        subjectId, label: watchLabel.trim(), terms: terms.trim(),
        platforms: picked.join(','),
        velocityEngagements, velocityMinutes, pollSeconds,
      })
      setAdding(false); setWatchLabel(''); setTerms('')
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Radar"
        description="What we listen for. A watch fires when a conversation accelerates, not
                     merely when a name appears."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 p-2">
          <p className="text-[11px] text-muted-foreground">
            Polling replaces a streaming bus on purpose: the gap between hearing and answering
            is a person reading a thread, and that is minutes, not milliseconds.
          </p>
          <button type="button" onClick={() => setAdding((v) => !v)}
                  className="rounded border border-border px-2 py-1 text-[11px]">
            {adding ? 'Close' : 'Add a watch'}
          </button>
        </div>

        {adding && (
          <form onSubmit={create} className="space-y-2 border-t border-border p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Whose conversation
                </span>
                <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)}
                        required
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.displayName}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Call it
                </span>
                <input value={watchLabel} onChange={(e) => setWatchLabel(e.target.value)}
                       required placeholder="Name and misspellings"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Terms, comma separated
              </span>
              <input value={terms} onChange={(e) => setTerms(e.target.value)} required
                     placeholder="Caroline Mwangi, @cmwangi, Mwangi 2027"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
              <span className="text-[10px] text-muted-foreground">
                Include the misspellings and the nicknames. An attack rarely uses the name on
                the ballot paper.
              </span>
            </label>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Where to listen
              </span>
              {/*
                Aggregators are deliberately NOT in this list.

                This list is WHERE A POST WAS SAID, not how we came to have it. TikHub reaches X,
                TikTok, Instagram and Reddit through one key — but a tweet it fetches was still
                said on X, and that is what the ledger attributes, what the drafter writes for
                and what an operator reads. Putting the aggregator here as a tickable option
                would relabel every thread with the name of a vendor.

                So you tick the platforms, and the room uses the aggregator automatically
                wherever it reaches one. The line below exists because the absence is otherwise
                indistinguishable from an oversight — which is exactly how it was read.
              */}
              <span className="text-[10px] text-muted-foreground">
                Pick the platforms a post was made on. If an aggregator is configured under
                Integrations, it is used to reach them automatically — it is a route, not a
                place, so it does not appear here.
              </span>
              <div className="mt-1 flex flex-wrap gap-2">
                {platforms.filter((p) => p.code !== 'MANUAL').map((p) => (
                  <label key={p.code} className="flex items-center gap-1 text-[11px]">
                    <input type="checkbox" checked={picked.includes(p.code)}
                           onChange={(e) => setPicked((c) => e.target.checked
                             ? [...c, p.code] : c.filter((x) => x !== p.code))} />
                    {p.label}
                  </label>
                ))}
                {platforms.filter((p) => p.code !== 'MANUAL').length === 0 && (
                  <span className="text-[11px] text-muted-foreground">
                    No platform driver is configured, so a watch can only be a placeholder for
                    now.
                  </span>
                )}
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Engagements
                </span>
                <input type="number" min={1} value={velocityEngagements}
                       onChange={(e) => setVelocityEngagements(Number(e.target.value))}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Within minutes
                </span>
                <input type="number" min={1} value={velocityMinutes}
                       onChange={(e) => setVelocityMinutes(Number(e.target.value))}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Check every (seconds)
                </span>
                <input type="number" min={60} step={30} value={pollSeconds}
                       onChange={(e) => setPollSeconds(Number(e.target.value))}
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
              </label>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Fires at <b>{velocityEngagements}</b> engagements inside{' '}
              <b>{velocityMinutes}</b> minutes. Set it against how much this subject is normally
              talked about — a threshold that fires constantly is one nobody reads.
            </p>
            <button type="submit" disabled={busy || !watchLabel.trim() || !terms.trim()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Saving…' : 'Start listening'}
            </button>
          </form>
        )}
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Watch</Th>
          <Th className="axis-hide-sm">Terms</Th>
          <Th>Fires at</Th>
          <Th className="axis-hide-sm">Last check</Th>
          <Th>State</Th>
        </>}>
          {watches.length === 0 && (
            <EmptyRow colSpan={5}
                      message="Nothing is being listened for. Threads can still be captured by
                               hand." />
          )}
          {watches.map((w) => (
            <tr key={w.id}>
              <Td>
                <span className="font-medium">{w.label}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {w.subjectName ?? 'unnamed subject'} · {w.platforms}
                </span>
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">{w.terms}</span>
              </Td>
              <Td>
                <span className="text-[11px]">
                  {w.velocityEngagements} in {w.velocityMinutes}m
                </span>
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">
                  {w.lastPolledAt ? fmtRelative(w.lastPolledAt) : 'never'}
                </span>
                {/*
                  PARTIAL is rendered as loudly as ERROR, in amber rather than red.

                  It means some platforms polled and some did not — typically a watch ticked for
                  seven platforms on a deployment that has credentials for one. Before the poller
                  isolated platforms, the first failure ended the whole poll, so this state could
                  not occur; now that it can, showing nothing for it would recreate exactly the
                  silence this column exists to break. The watch looks healthy while most of its
                  platforms are doing nothing.
                */}
                {w.lastStatus === 'ERROR' && (
                  <span className="block text-[10px] text-rose-600">
                    {w.lastError ?? 'the last check failed — silence here is not calm'}
                  </span>
                )}
                {w.lastStatus === 'PARTIAL' && (
                  <span className="block text-[10px] text-amber-700 dark:text-amber-400">
                    Some platforms did not run: {w.lastError}
                  </span>
                )}
              </Td>
              <Td>
                <button type="button" disabled={busy}
                        onClick={() => {
                          setBusy(true)
                          {/*
                            The WHOLE row, not `{ active }`. `POST /watches/{id}` is a full
                            replace and subjectId, label, terms and platforms are all required,
                            so a partial body was rejected outright — this button raised a 400
                            and did nothing. Had it been accepted it would have blanked the
                            watch's terms on the way to flipping one boolean.
                          */}
                          void updateWatch(w.id, {
                            subjectId: w.subjectId,
                            label: w.label,
                            terms: w.terms,
                            platforms: w.platforms,
                            velocityEngagements: w.velocityEngagements,
                            velocityMinutes: w.velocityMinutes,
                            pollSeconds: w.pollSeconds,
                            active: !w.active,
                          })
                            .then(load)
                            .catch((err) => setError(explain(err)))
                            .finally(() => setBusy(false))
                        }}
                        className="rounded border border-border px-2 py-0.5 text-[10px]">
                  {w.active ? 'Listening — pause' : 'Paused — resume'}
                </button>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
